import React, { useState } from 'react';
import { GradientModal, MonthDayPicker, IconPickerModal, SeasonPicker } from '../ui';
import { TYPES } from './constants';
import { getCategoryOptions, getFormConfig, getResetFieldsForType } from './utils';
import { getPlantIcon, getIconSrc, SPACING_MAX_CHARS } from '../../utils';

/**
 * Modal for editing an existing seed/plant entry.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Object} props.formData - Form state object
 * @param {Function} props.onFormChange - Handler for form field changes
 * @param {Function} props.onSave - Handler for saving the entry
 * @param {Function} props.onClose - Handler for closing the modal
 */
const EditSeedModal = ({
  isOpen,
  formData,
  onFormChange,
  onSave,
  onClose,
  zIndex,
}) => {
  const [showIconPicker, setShowIconPicker] = useState(false);

  if (!isOpen) return null;

  const editFormConfig = getFormConfig(formData.type, formData.category);
  const editCategoryOptions = getCategoryOptions(formData.type);

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    const resetFields = getResetFieldsForType(newType, formData);
    onFormChange({ ...formData, type: newType, ...resetFields });
  };

  return (
    <GradientModal
      isOpen={true}
      onClose={onClose}
      title="Edit Entry"
      primaryAction={{ label: 'Save', onClick: onSave }}
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      {...(zIndex ? { zIndex } : {})}
    >
      {/* Row 1: Type, Category, Plant, Variety */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">
            Type <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
          </label>
          <select
            value={formData.type}
            onChange={handleTypeChange}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
          >
            {TYPES.map(type => (
              <option key={type.id} value={type.id}>{type.emoji} {type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">
            Category <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
          </label>
          <select
            value={formData.category}
            onChange={(e) => onFormChange({ ...formData, category: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
          >
            <option value="">Select category...</option>
            {editCategoryOptions.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.emoji} {cat.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">
            Plant Name <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
          </label>
          <input
            type="text"
            value={formData.plant}
            onChange={(e) => onFormChange({ ...formData, plant: e.target.value })}
            placeholder="e.g., Tomato"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">
            Variety
          </label>
          <input
            type="text"
            value={formData.variety}
            onChange={(e) => onFormChange({ ...formData, variety: e.target.value })}
            placeholder="e.g., Roma (optional)"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
          />
        </div>
      </div>

      {/* Icon selector */}
      <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-xl">
        <img
          src={getIconSrc(formData.icon || getPlantIcon(formData.plant, null, formData.variety))}
          alt=""
          width={36}
          height={36}
          draggable={false}
          onError={(e) => { e.target.style.visibility = 'hidden'; }}
        />
        <div className="flex-1">
          <span className="text-sm font-medium text-[#2F5233]">Plant Icon</span>
          <p className="text-xs text-gray-500">{(formData.icon || getPlantIcon(formData.plant, null, formData.variety) || '').replace(/-/g, ' ')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowIconPicker(true)}
          className="px-3 py-1.5 text-sm font-medium text-[#52A675] hover:bg-[#52A675]/10 rounded-lg transition-colors"
        >
          Change
        </button>
      </div>

      {/* Icon picker nested modal */}
      <IconPickerModal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSelect={(iconName) => onFormChange({ ...formData, icon: iconName })}
        currentIcon={formData.icon || getPlantIcon(formData.plant, null, formData.variety)}
      />

      {/* PLANTING INFORMATION Section - Only for Seeds/Annuals */}
      {editFormConfig.showSeason && (
        <div className="mb-6">
          <div className="border-b border-[#e8f5e9] pb-1.5 mb-1">
            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planting Information</span>
          </div>
          <p className="text-xs text-gray-500 italic mb-3">Growing habits and scheduling info — used to power planting estimates and garden recommendations</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Season <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
              </label>
              <select
                value={formData.season}
                onChange={(e) => onFormChange({ ...formData, season: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select season...</option>
                <option value="warm">☀️ Warm</option>
                <option value="cool">❄️ Cool</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Cool = spring/fall, Warm = summer</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                {editFormConfig.productionLabel}
              </label>
              <input
                type="number"
                min="0"
                value={formData.days_to_production}
                onChange={(e) => onFormChange({ ...formData, days_to_production: e.target.value })}
                placeholder="e.g., 75"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Typical start method
              </label>
              <select
                value={formData.preferred_start_method}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onFormChange({
                    ...formData,
                    preferred_start_method: newValue,
                    // Clear weeks_indoors and planned_transplant_date when switching to direct_sow
                    weeks_indoors_before_transplant: newValue === 'direct_sow' ? '' : formData.weeks_indoors_before_transplant,
                    planned_transplant_date: newValue === 'direct_sow' ? '' : formData.planned_transplant_date,
                    // Clear direct_sow_weeks when switching to indoor (field is disabled but value isn't auto-cleared)
                    direct_sow_weeks_before_last_frost: newValue === 'indoor' ? '' : formData.direct_sow_weeks_before_last_frost
                  });
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select... (optional)</option>
                <option value="indoor">🌱 Start Indoors</option>
                <option value="direct_sow">🌿 Direct Sow</option>
              </select>
            </div>

            <div className={formData.preferred_start_method === 'direct_sow' ? 'opacity-50' : ''}>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Start indoors (weeks)
              </label>
              <input
                type="number"
                min="0"
                value={formData.weeks_indoors_before_transplant}
                onChange={(e) => onFormChange({ ...formData, weeks_indoors_before_transplant: e.target.value })}
                placeholder="e.g., 6"
                disabled={formData.preferred_start_method === 'direct_sow'}
                title={formData.preferred_start_method === 'direct_sow' ? 'Not needed for direct sow crops' : ''}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] ${formData.preferred_start_method === 'direct_sow' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              {formData.preferred_start_method === 'direct_sow' && (
                <p className="text-xs text-gray-400 mt-1">Not needed for direct sow crops</p>
              )}
            </div>

            {/* Sow outdoors (weeks before last frost) — pair to start indoors */}
            <div className={formData.preferred_start_method === 'indoor' ? 'opacity-50' : ''}>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Sow outdoors (wks before last frost)
              </label>
              <input
                type="number"
                min="0"
                value={formData.direct_sow_weeks_before_last_frost || ''}
                onChange={(e) => onFormChange({ ...formData, direct_sow_weeks_before_last_frost: e.target.value })}
                placeholder="e.g., 4"
                disabled={formData.preferred_start_method === 'indoor'}
                title={formData.preferred_start_method === 'indoor' ? 'Not needed for indoor-started crops' : ''}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] ${formData.preferred_start_method === 'indoor' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
              />
              {formData.preferred_start_method === 'indoor' && (
                <p className="text-xs text-gray-400 mt-1">Not needed for indoor-started crops</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Spacing
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.spacing || ''}
                  onChange={(e) => onFormChange({ ...formData, spacing: e.target.value.slice(0, SPACING_MAX_CHARS) })}
                  placeholder="e.g. 12 inches"
                  maxLength={SPACING_MAX_CHARS}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
                />
                {formData.spacing && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {formData.spacing.length}/{SPACING_MAX_CHARS}
                  </span>
                )}
              </div>
            </div>

            {/* Sun requirement + Water needs */}
            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">Sun</label>
              <select
                value={formData.sun_requirement || ''}
                onChange={(e) => onFormChange({ ...formData, sun_requirement: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select... (optional)</option>
                <option value="full_sun">☀️ Full Sun</option>
                <option value="part_sun">⛅ Part Sun</option>
                <option value="shade_tolerant">🌥️ Shade Tolerant</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">Water Needs</label>
              <select
                value={formData.water_needs || ''}
                onChange={(e) => onFormChange({ ...formData, water_needs: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select... (optional)</option>
                <option value="low">💧 Low</option>
                <option value="moderate">💧💧 Moderate</option>
                <option value="high">💧💧💧 High</option>
              </select>
            </div>

            {/* Harvest season + Frost tolerant — outlier fields, always last */}
            <div>
              <SeasonPicker
                value={Array.isArray(formData.harvest_season) ? formData.harvest_season : []}
                onChange={(seasons) => onFormChange({ ...formData, harvest_season: seasons })}
                isFlower={formData.category === 'flower'}
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.frost_tolerant || false}
                  onChange={(e) => onFormChange({ ...formData, frost_tolerant: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#52A675] focus:ring-[#52A675]"
                />
                <span className="text-sm font-semibold text-[#2F5233]">Frost Tolerant</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Perennial/Tree/Bush-specific planting info (harvest season + years to production) */}
      {!editFormConfig.showSeason && (
        <div className="mb-6">
          <div className="border-b border-[#e8f5e9] pb-1.5 mb-1">
            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planting Information</span>
          </div>
          <p className="text-xs text-gray-500 italic mb-3">Used to estimate planting dates and harvest predictions</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <SeasonPicker
                value={Array.isArray(formData.harvest_season) ? formData.harvest_season : []}
                onChange={(seasons) => onFormChange({ ...formData, harvest_season: seasons })}
                isFlower={formData.category === 'flower'}
              />
            </div>

            {editFormConfig.showYearsToProduction && (
              <div>
                <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                  {editFormConfig.yearsLabel}
                </label>
                <input
                  type="text"
                  value={formData.years_to_production}
                  onChange={(e) => onFormChange({ ...formData, years_to_production: e.target.value })}
                  placeholder="e.g., 2 or 3-5"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
                />
              </div>
            )}

            {/* Sun requirement + Water needs — relevant for all plant types */}
            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">Sun</label>
              <select
                value={formData.sun_requirement || ''}
                onChange={(e) => onFormChange({ ...formData, sun_requirement: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select... (optional)</option>
                <option value="full_sun">☀️ Full Sun</option>
                <option value="part_sun">⛅ Part Sun</option>
                <option value="shade_tolerant">🌥️ Shade Tolerant</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">Water Needs</label>
              <select
                value={formData.water_needs || ''}
                onChange={(e) => onFormChange({ ...formData, water_needs: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
              >
                <option value="">Select... (optional)</option>
                <option value="low">💧 Low</option>
                <option value="moderate">💧💧 Moderate</option>
                <option value="high">💧💧💧 High</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* PLANNING Section - Only for Seeds/Annuals */}
      {editFormConfig.showSeason && (
        <div className="mb-6">
          <div className="border-b border-[#e8f5e9] pb-1.5 mb-1">
            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planning</span>
          </div>
          <p className="text-xs text-gray-500 italic mb-3">Know when you always plant and transplant? Add those dates here for automatic scheduling</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Planned Start Date
              </label>
              <MonthDayPicker
                value={formData.planned_start_date || ''}
                onChange={(val) => onFormChange({ ...formData, planned_start_date: val })}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Planned Transplant Date
              </label>
              <MonthDayPicker
                value={formData.planned_transplant_date || ''}
                onChange={(val) => onFormChange({ ...formData, planned_transplant_date: val })}
                disabled={formData.preferred_start_method === 'direct_sow'}
              />
              {formData.preferred_start_method === 'direct_sow' && (
                <p className="text-xs text-gray-400 mt-1">Not needed for direct sow crops</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* INVENTORY TRACKING Section */}
      <div className="mb-6">
        <div className="border-b border-[#e8f5e9] pb-1.5 mb-3">
          <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Inventory Tracking</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-[#2F5233] mb-2">
              Quantity
            </label>
            <input
              type="text"
              value={formData.quantity}
              onChange={(e) => onFormChange({ ...formData, quantity: e.target.value })}
              placeholder="e.g., 25 seeds, 1 packet"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
            />
          </div>

          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer mt-7">
              <input
                type="checkbox"
                checked={formData.need_to_buy}
                onChange={(e) => onFormChange({ ...formData, need_to_buy: e.target.checked })}
                className="w-5 h-5 text-[#52A675] border-gray-300 rounded focus:ring-[#52A675]"
              />
              <span className="text-sm font-semibold text-[#2F5233]">Need to buy</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => onFormChange({ ...formData, notes: e.target.value })}
            rows="2"
            placeholder="Optional notes"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] resize-y min-h-[60px]"
          />
        </div>
      </div>
    </GradientModal>
  );
};

export default EditSeedModal;
